import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useApiClient } from "../../../apiClient";
import { CourtDto } from "../../../models/court/dto/court.dto";
import { GET_LOCATION_COURTS_QUERY_KEY } from "../../court-management/hooks/useGetCourts";
import { useAvailableLocations } from "../../location-management/hooks/useAvailableLocations";

/**
 * Every court in the SELECTED ORGANISATION, flattened across its locations.
 *
 * Venue agents are org-scoped but the courts API is location-scoped
 * (`GET courts?locationId=`), and an org can hold several locations — a single
 * `useGetCourts(selectedLocation)` would silently hide courts an agent can
 * legitimately be provisioned for. This fans the EXISTING endpoint out over
 * `useAvailableLocations()` and reuses `GET_LOCATION_COURTS_QUERY_KEY`, so the
 * results share a cache with the court-management pages rather than
 * duplicating them under a new key.
 */
export const useGetOrganizationCourts = () => {
    const client = useApiClient();
    // `isInitialLoading` (D62): the locations read is
    // `enabled: Boolean(organizationId)`, so its raw `isLoading` never clears
    // while no organization is selected — and this flag is OR'd into the one
    // this hook exports, which `VenueAgentsPage` passes down as
    // `isLoadingCourts` to disable a Save button and put a court Select into
    // its loading state. The raw flag disables those controls permanently.
    const { data: locations, isInitialLoading: isLoadingLocations } = useAvailableLocations();
    const locationIds = useMemo(
        () => (locations ?? []).map((location) => location.id),
        [locations],
    );

    const results = useQueries({
        queries: locationIds.map((locationId) => ({
            staleTime: 5 * 60 * 1000,
            queryKey: GET_LOCATION_COURTS_QUERY_KEY(locationId),
            queryFn: () => client.fetchApi<CourtDto[], {}>({
                endpoint: `courts?locationId=${locationId}`,
                method: 'GET',
            }),
        })),
    });

    // `results` is a fresh array identity on every render, so the memo is keyed
    // on the derived payload instead — otherwise every consumer re-renders on
    // every parent render.
    const flattened = results.flatMap((result) => result.data ?? []);
    const signature = flattened.map((court) => `${court.id}:${court.name}`).join('|');

    const courts = useMemo(
        () => {
            // Two locations never share a court id, but a duplicated fetch
            // (cache hydration racing a refetch) can still yield repeats.
            const byId = new Map<number, CourtDto>();
            flattened.forEach((court) => byId.set(court.id, court));
            return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [signature],
    );

    return {
        courts,
        isLoading: isLoadingLocations || results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
    };
};
